import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/AuthContext";
import { Save, Loader, X } from "lucide-react";
import { LanguageSelector } from "../../components/LanguageSelector";
import { useFriends } from "../../hooks/useFriends";
import { useToast } from "../../context/ToastContext";
import { api } from "../../api/clientInstance";
import StatsBar from "../../components/common/StatsBar";
import "./ProfilePage.css";

// Same field image as the dashboard/cups (served from cache — no extra fetch).
const FIELD_BG = `linear-gradient(rgba(0,0,0,0.75), rgba(0,0,0,0.75)), url(${process.env.PUBLIC_URL}/images/field_bg.webp)`;

const COUNTRIES = [
  ["AF","Afghanistan"],["AL","Albania"],["DZ","Algeria"],["AR","Argentina"],
  ["AU","Australia"],["AT","Austria"],["BE","Belgium"],["BR","Brazil"],
  ["CA","Canada"],["CL","Chile"],["CN","China"],["CO","Colombia"],
  ["HR","Croatia"],["CZ","Czech Republic"],["DK","Denmark"],["EG","Egypt"],
  ["FI","Finland"],["FR","France"],["DE","Germany"],["GR","Greece"],
  ["HU","Hungary"],["IN","India"],["ID","Indonesia"],["IE","Ireland"],
  ["IL","Israel"],["IT","Italy"],["JP","Japan"],["MX","Mexico"],
  ["NL","Netherlands"],["NZ","New Zealand"],["NG","Nigeria"],["NO","Norway"],
  ["PK","Pakistan"],["PE","Peru"],["PH","Philippines"],["PL","Poland"],
  ["PT","Portugal"],["RO","Romania"],["RU","Russia"],["SA","Saudi Arabia"],
  ["ZA","South Africa"],["KR","South Korea"],["ES","Spain"],["SE","Sweden"],
  ["CH","Switzerland"],["TH","Thailand"],["TR","Turkey"],["UA","Ukraine"],
  ["GB","United Kingdom"],["US","United States"],["UY","Uruguay"],["VE","Venezuela"],
];


function FriendsSection() {
  const { t } = useTranslation();
  const toast = useToast();
  const { friends, pending, loading, send, accept, reject, remove } = useFriends();
  const [search, setSearch]           = useState("");
  const [searching, setSearching]     = useState(false);
  const [searchError, setSearchError] = useState("");
  const [confirmingRemove, setConfirmingRemove] = useState(null);

  const handleSend = async () => {
    const name = search.trim();
    if (!name) return;
    setSearching(true);
    setSearchError("");
    try {
      await send(name);
      setSearch("");
      toast(t("friends.requestSent", { name }), "success");
    } catch (e) {
      setSearchError(e.message);
    } finally {
      setSearching(false);
    }
  };

  return (
    <>
      {pending.length > 0 && (
        <div className="friends-pending">
          {pending.map((u) => (
            <div key={u.id} className="friend-row friend-row--pending">
              <span className="friend-name">{u.name}</span>
              <button className="friend-btn friend-btn--accept" onClick={() => accept(u.id)}>✓</button>
              <button className="friend-btn friend-btn--reject" onClick={() => reject(u.id)}>✕</button>
            </div>
          ))}
        </div>
      )}

      {loading
        ? <p className="profile-empty">{t("friends.loading")}</p>
        : friends.length === 0 && pending.length === 0
        ? <p className="profile-empty">{t("profile.noFriends")}</p>
        : friends.map((u) => (
            <div key={u.id} className="friend-row">
                  <span className="friend-name">{u.name}</span>
              {confirmingRemove === u.id ? (
                <>
                  <span className="friend-confirm-label">{t("friends.confirmRemove", { name: u.name })}</span>
                  <button className="friend-btn friend-btn--accept" onClick={() => { remove(u.id); setConfirmingRemove(null); }}>✓</button>
                  <button className="friend-btn friend-btn--reject" onClick={() => setConfirmingRemove(null)}>✕</button>
                </>
              ) : (
                <button className="friend-btn friend-btn--remove" onClick={() => setConfirmingRemove(u.id)} title={t("friends.remove")}>✕</button>
              )}
            </div>
          ))
      }

      <div className="friend-search-row">
        <input
          className={`friend-search-input${searchError ? " input-error" : ""}`}
          placeholder={t("friends.searchPlaceholder")}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setSearchError(""); }}
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
          disabled={searching}
        />
        <button className="friend-btn friend-btn--add" onClick={handleSend} disabled={searching || !search.trim()}>+</button>
      </div>
      {searchError && <p className="friend-error">{searchError}</p>}
    </>
  );
}

function EmailAdder() {
  const { t } = useTranslation();
  const { setUser } = useAuth();
  const [email, setEmail]   = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");
  const [done, setDone]     = useState(false);

  const handleSave = async () => {
    const value = email.trim();
    if (!value) return;
    setSaving(true);
    setError("");
    try {
      const updated = await api("/user/me/email", { method: "PATCH", body: JSON.stringify({ email: value }) });
      setUser(updated);
      setDone(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (done) return <p className="profile-email-done">{t("profile.emailSaved")}</p>;

  return (
    <div className="country-picker">
      <input
        type="email"
        className="country-select"
        placeholder={t("profile.emailPlaceholder")}
        value={email}
        onChange={(e) => { setEmail(e.target.value); setError(""); }}
        onKeyDown={(e) => e.key === "Enter" && handleSave()}
        disabled={saving}
      />
      <button className="team-save-btn" onClick={handleSave} disabled={!email.trim() || saving}>
        {saving ? <Loader size={16} className="spin" /> : <Save size={16} />}
      </button>
      {error && <p className="profile-error">{error}</p>}
    </div>
  );
}

function CountryPicker() {
  const { t }  = useTranslation();
  const { setUser } = useAuth();
  const [selected, setSelected] = useState("");
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    setError("");
    try {
      const updated = await api("/user/me/country", { method: "PATCH", body: JSON.stringify({ country: selected }) });
      setUser(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="country-picker">
      <select
        className="country-select"
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
      >
        <option value="">{t("profile.selectCountry")}</option>
        {COUNTRIES.map(([code, name]) => (
          <option key={code} value={code}>{name}</option>
        ))}
      </select>
      <button className="team-save-btn" onClick={handleSave} disabled={!selected || saving}>
        {saving ? <Loader size={16} className="spin" /> : <Save size={16} />}
      </button>
      {error && <p className="profile-error">{error}</p>}
    </div>
  );
}

function FeedbackSection() {
  const { t } = useTranslation();
  const [text,    setText]    = useState("");
  const [sending, setSending] = useState(false);
  const [done,    setDone]    = useState(false);
  const [error,   setError]   = useState("");

  const MAX = 300;

  const handleSend = async () => {
    if (!text.trim()) return;
    setSending(true);
    setError("");
    try {
      await api("/feedback", { method: "POST", body: JSON.stringify({ message: text.trim() }) });
      setDone(true);
      setText("");
    } catch (e) {
      setError(e.status === 429 ? t("profile.feedbackLimit") : e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="profile-feedback">
      <p className="profile-account-label">{t("profile.feedbackTitle")}</p>
      {done ? (
        <p className="profile-feedback-done">{t("profile.feedbackSent")}</p>
      ) : (
        <>
          <textarea
            className="profile-feedback-textarea"
            placeholder={t("profile.feedbackPlaceholder")}
            maxLength={MAX}
            value={text}
            onChange={(e) => { setText(e.target.value); setError(""); }}
            rows={3}
          />
          <div className="profile-feedback-footer">
            <span className="profile-feedback-count">{text.length}/{MAX}</span>
            <button className="profile-save-btn" onClick={handleSend} disabled={sending || !text.trim()}>
              {sending ? "…" : t("profile.feedbackSend")}
            </button>
          </div>
          {error && <p className="profile-feedback-error">{error}</p>}
        </>
      )}
    </div>
  );
}

function ChangePasswordSection() {
  const { t } = useTranslation();
  const [open, setOpen]           = useState(false);
  const [current, setCurrent]     = useState("");
  const [next, setNext]           = useState("");
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");
  const [done, setDone]           = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setError(""); setDone(false); };

  const handleOpen = () => { reset(); setOpen(true); };
  const handleCancel = () => { setOpen(false); reset(); };

  const handleSave = async () => {
    if (!current || !next) return;
    setSaving(true);
    setError("");
    try {
      await api("/user/me/password", {
        method: "PATCH",
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      setDone(true);
      setCurrent("");
      setNext("");
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button className="profile-action-btn" onClick={handleOpen}>
        {t("profile.changePassword")}
      </button>
    );
  }

  if (done) {
    return (
      <div className="profile-account-row profile-account-row--col">
        <span className="profile-account-label">{t("profile.changePassword")}</span>
        <p className="profile-email-done">{t("profile.passwordSaved")}</p>
      </div>
    );
  }

  return (
    <div className="profile-account-row profile-account-row--col">
      <span className="profile-account-label">{t("profile.changePassword")}</span>
      <div className="country-picker">
        <input
          type="password"
          className="country-select"
          placeholder={t("profile.currentPassword")}
          value={current}
          onChange={(e) => { setCurrent(e.target.value); setError(""); }}
          disabled={saving}
          autoComplete="current-password"
        />
      </div>
      <div className="country-picker">
        <input
          type="password"
          className="country-select"
          placeholder={t("profile.newPassword")}
          value={next}
          onChange={(e) => { setNext(e.target.value); setError(""); }}
          onKeyDown={(e) => e.key === "Enter" && handleSave()}
          disabled={saving}
          autoComplete="new-password"
        />
        <button className="team-save-btn" onClick={handleSave} disabled={!current || !next || saving}>
          {saving ? <Loader size={16} className="spin" /> : <Save size={16} />}
        </button>
        <button className="team-save-btn" onClick={handleCancel} disabled={saving}><X size={16} /></button>
      </div>
      {error && <p className="profile-error">{error}</p>}
    </div>
  );
}

function TrophiesSection() {
  const { t } = useTranslation();
  const [trophies, setTrophies] = useState([]);

  useEffect(() => {
    api("/cup/trophies").then(setTrophies).catch(() => {});
  }, []);

  if (!trophies.length) return null;

  return (
    <div className="profile-trophies">
      <p className="profile-account-label">{t("cup.trophies")}</p>
      {trophies.map((c) => (
        <div key={c.id} className="profile-trophy-row">
          <span className="profile-trophy-icon">🏆</span>
          <span className="profile-trophy-name">{c.name}</span>
          <span className="profile-trophy-date">
            {c.date ? new Date(c.date).toLocaleDateString() : ""}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function ProfilePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [tab, setTab]     = useState("club");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api("/user/stats").then(setStats).catch(() => {});
  }, []);

  return (
    <div className="profile-bg" style={{ backgroundImage: FIELD_BG }}>
    <div className="profile">
      <div className="profile-header">
        <div className="profile-avatar">{user?.name?.[0]?.toUpperCase() ?? "?"}</div>
        <div className="profile-header-info">
          <h2 className="profile-name">{user?.name}</h2>
        </div>
      </div>

      {stats && (
        <div className="profile-stats">
          <StatsBar stats={stats} size="md" />
        </div>
      )}

      <TrophiesSection />

      <div className="profile-tabs">
        <button className={`profile-tab ${tab === "club" ? "profile-tab--active" : ""}`} onClick={() => setTab("club")}>
          {t("profile.tabClub")}
        </button>
        <button className={`profile-tab ${tab === "settings" ? "profile-tab--active" : ""}`} onClick={() => setTab("settings")}>
          {t("profile.tabSettings")}
        </button>
      </div>

      {tab === "club" && (
        <div className="profile-section">
          <FriendsSection />
        </div>
      )}

      {tab === "settings" && (
        <div className="profile-section">
          {!user?.country && (
            <div className="profile-account-row profile-account-row--col">
              <span className="profile-account-label">{t("profile.country")}</span>
              <CountryPicker />
            </div>
          )}
          {user?.country && (
            <div className="profile-account-row">
              <span className="profile-account-label">{t("profile.country")}</span>
              <span className="profile-country-value">{COUNTRIES.find(([c]) => c === user.country)?.[1] ?? user.country}</span>
            </div>
          )}
          {!user?.email && (
            <div className="profile-account-row profile-account-row--col">
              <span className="profile-account-label">{t("profile.addEmail")}</span>
              <EmailAdder />
            </div>
          )}
          {user?.email && (
            <div className="profile-account-row">
              <span className="profile-account-label">{t("profile.account")}</span>
              <span className="profile-country-value">
                {user.email_verified ? `✓ ${user.email}` : t("profile.emailPendingVerification")}
              </span>
            </div>
          )}
          <div className="profile-account-row">
            <span className="profile-account-label">{t("profile.language")}</span>
            <LanguageSelector />
          </div>
          <ChangePasswordSection />
          <FeedbackSection />
        </div>
      )}
    </div>
    </div>
  );
}
